/**
 * Keep the chat launcher clear of whatever is sitting at the bottom of the page.
 *
 * The first version lifted it by a fixed 260px whenever the consent banner was up,
 * below a 1024px breakpoint. Three things were wrong with that, and all three were
 * found by measuring rather than reading:
 *
 * - **The banner's height depends on the language.** Below the library's own
 *   breakpoint it goes full-width with its buttons stacked, so the German and
 *   Lithuanian copy is markedly taller than the English. At 375px a 260px lift put
 *   the launcher inside the banner body.
 * - **The preferences dialog was not covered at all** — the rule keyed on
 *   `show--consent` only, so the modal reachable from the banner (and later from the
 *   footer) had the bubble floating over its Save button at every width.
 * - **The language nudge was not covered either**, and it is the one collision the
 *   spec actually names. It appears exactly when the consent banner goes away, which
 *   is when the old rule stopped lifting.
 *
 * So the lift is measured from the elements that are really there, and written to a
 * custom property the stylesheet reads. Nothing to keep in step with a locale.
 */

const PROPERTY = '--support-chat-lift';
const GAP = 16;

/** Anything that can sit under the launcher, in the order it is cheapest to find. */
const SELECTORS = [
  '#cc-main .cm', // consent banner
  '#cc-main .pm', // preferences dialog
  '[data-support-chat-lift]', // the language nudge marks itself
];

/**
 * Where the launcher actually is, horizontally.
 *
 * Measuring vertical extent alone made the launcher step aside for obstacles it
 * never touches: the consent banner sits bottom-*centre* and the language nudge
 * bottom-*left*, while the launcher is bottom-right, so at desktop widths they
 * share no pixels and the bubble was still pushed ~80px up. The one width the
 * placement test covers, 375px, is the one width where the collision is real.
 *
 * Read off the bubble when it exists; before it does — the tracker is installed
 * before `run()` — fall back to the band Chatwoot's own stylesheet places it in,
 * which is what the launcher will occupy a moment later.
 */
function launcherBand(): { left: number; right: number } {
  const bubble = document.querySelector<HTMLElement>('.woot-widget-bubble');
  if (bubble) {
    const box = bubble.getBoundingClientRect();
    if (box.width > 0) return { left: box.left, right: box.right };
  }
  const FALLBACK_WIDTH = 64;
  const FALLBACK_INSET = 20;
  return {
    left: window.innerWidth - FALLBACK_INSET - FALLBACK_WIDTH,
    right: window.innerWidth - FALLBACK_INSET,
  };
}

function measure(): number {
  let lift = 0;
  const launcher = launcherBand();
  for (const selector of SELECTORS) {
    for (const node of document.querySelectorAll<HTMLElement>(selector)) {
      const box = node.getBoundingClientRect();
      // Nothing to step aside from if the two never meet horizontally.
      if (box.right <= launcher.left || box.left >= launcher.right) continue;
      // Only what is actually on screen and actually at the bottom. The consent
      // library keeps BOTH surfaces in the DOM when dismissed — the dialog as a
      // zero-height box, the banner at full size behind `visibility: hidden` and
      // `opacity: 0` — and a box the visitor cannot see must not hold the
      // launcher two hundred pixels up on every page after the banner is
      // answered.
      if (box.height === 0 || box.bottom < window.innerHeight - box.height) continue;
      const style = window.getComputedStyle(node);
      if (style.visibility === 'hidden' || style.display === 'none' || style.opacity === '0') {
        continue;
      }
      lift = Math.max(lift, window.innerHeight - box.top + GAP);
    }
  }
  return lift;
}

/**
 * When the obstacle is (near) full-screen — the preferences dialog on a phone,
 * the centred desktop dialog — no offset places the launcher anywhere sane: the
 * old clamp flung it to the TOP of the screen, over the dialog's title and
 * close control. Past this point the launcher stands down instead: the bubble
 * slides just below the viewport until the surface goes away.
 */
const STAND_DOWN = -96;

function placement(): number | null {
  const lift = measure();
  if (lift === 0) return null;
  if (lift > Math.max(window.innerHeight - 160, 0)) return STAND_DOWN;
  return lift;
}

export function trackLauncherLift(): () => void {
  if (typeof window === 'undefined') return () => {};

  let settle = 0;
  let lastApplied: number | null = null;
  // How many re-checks have run since this WAKE. The re-check exists for a
  // dismissal that finishes with no mutation and no transition event, and that is
  // by definition the case where the value comes back the same — so arming it only
  // `if (value !== lastApplied)`, as it first was, meant it could never fire for
  // its own purpose.
  //
  // Counting from the last value CHANGE was the next mistake and a worse one: the
  // counter is monotonic while the value holds, so it passed 3 within two seconds
  // of load and the tracker went timer-free for the rest of the session. The
  // dismissal at t=8s then woke it, measured mid-fade, saw the same value, and
  // armed nothing — the stale lift stuck for good, which is the exact failure the
  // re-check exists to prevent.
  //
  // Counted from the wake instead: every external wake — a mutation, a resize, a
  // transition end — gets a fresh budget of re-checks, and only the re-checks
  // themselves spend it. So the chain always terminates, and it is always there
  // for the next real change.
  let steady = 0;
  const SETTLE_CHECKS = 3;

  const apply = () => {
    const value = placement();
    // The property is REMOVED when nothing needs clearing, not set to 0px: the
    // stylesheet keys on the property being present at all, and an
    // unconditional 0px overrode Chatwoot's own 20px inset on every ordinary
    // page — the launcher sat pinned to the viewport edge for most pageviews.
    // Absent property → Chatwoot's default resting position.
    if (value !== null) {
      document.documentElement.style.setProperty(PROPERTY, `${value}px`);
      // A placed-or-stood-down launcher re-checks itself shortly: dismissals can
      // finish with no mutation and no transition event to catch (a measurement
      // taken mid-fade sees the surface at partial opacity), and a stale value
      // would otherwise hold until something unrelated moved the DOM. Re-armed
      // only while the value is still moving — a steady state stops the timer,
      // and the observer wakes it on the next real change.
      window.clearTimeout(settle);
      // A change means the surface is still moving, so the budget resets and the
      // chain keeps following it.
      if (value !== lastApplied) steady = 0;
      if (steady < SETTLE_CHECKS) {
        steady += 1;
        settle = window.setTimeout(recheck, 600);
      }
    } else {
      window.clearTimeout(settle);
      steady = 0;
      document.documentElement.style.removeProperty(PROPERTY);
    }
    lastApplied = value;
  };

  // Coalesced to one measurement per frame. The observer below fires on every
  // DOM mutation of a site running an animation library, and measure() forces
  // layout — unthrottled, that is three querySelectorAll and a
  // getBoundingClientRect per mutation.
  let scheduled = false;
  let frame = 0;
  const measure = () => {
    if (scheduled) return;
    scheduled = true;
    frame = requestAnimationFrame(() => {
      scheduled = false;
      apply();
    });
  };
  /** Woken by something outside us, so the re-check budget starts over. */
  const schedule = () => {
    steady = 0;
    measure();
  };
  /** One of our own re-checks: it spends the budget rather than renewing it. */
  const recheck = () => measure();

  apply();
  // The banner is added and removed by the consent library, the nudge by React, and
  // both change height on rotation or a font swap.
  // Our OWN property write is a mutation on the observed node, so an unfiltered
  // callback re-entered on every placement — which is also what kept clearing the
  // settle timer a frame after it was armed.
  const observer = new MutationObserver((records) => {
    const ours = records.every(
      (r) =>
        r.type === 'attributes' &&
        r.target === document.documentElement &&
        r.attributeName === 'style',
    );
    if (!ours) schedule();
  });
  // Attributes too, not just structure: the consent library dismisses its
  // surfaces by flipping classes and inline styles on nodes it keeps in the
  // DOM, which is invisible to a childList observer — the measurement never
  // re-ran after Accept and the stale lift held the launcher mid-air. The
  // firehose this opens is drained by the frame-coalescing above.
  // documentElement, not body: the consent library shows and hides its surfaces
  // through classes on <html> (`show--consent`, `show--preferences`), and a
  // body-scoped observer never saw the dismissal at all.
  observer.observe(document.documentElement, { childList: true, subtree: true, attributes: true });
  window.addEventListener('resize', schedule);
  // The dismiss animations end without any DOM mutation — the class flips at the
  // START of the fade, so a measurement taken then still sees the surface at
  // partial opacity and the stale lift would hold until something else moved.
  document.addEventListener('transitionend', schedule, true);
  document.addEventListener('animationend', schedule, true);

  return () => {
    observer.disconnect();
    window.removeEventListener('resize', schedule);
    document.removeEventListener('transitionend', schedule, true);
    document.removeEventListener('animationend', schedule, true);
    window.clearTimeout(settle);
    // The queued frame too, and the flag with it. A `stop()` landing in the same
    // frame as a pending measurement let that callback run after teardown: it
    // re-wrote the property this line had just removed, and armed a fresh 600ms
    // chain in a closure nothing could reach any more — so the lift stayed on
    // `<html>` for good and kept forcing layout after the tracker was gone.
    cancelAnimationFrame(frame);
    scheduled = false;
    document.documentElement.style.removeProperty(PROPERTY);
  };
}
